/**
 * The pieces every executive screen is built from: tiles with their sources,
 * evidence chips, a table, the monthly strip, and the question box that
 * answers from the module's own engine first and lets a connected model in
 * only with its figures checked.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { askModel, hasModel, loadConfig } from "../../assistant/external";
import { verifyFigures, type SourceRef } from "../../domain/decision";
import { money } from "../../domain/intelligence";
import { Button, Card, Input } from "../../design/components";
import { t } from "../../i18n";
import s from "./Executive.module.css";

export { s as x };

/** A number on the desk with its sentence and its sources — the shape every engine emits. */
export interface Figure {
  key: string;
  label: string;
  value: number | null;
  unit: string;
  text: string;
  /** rests on the demonstration, a rate card or an entry */
  assumed?: boolean;
  tone?: "good" | "bad" | "warn";
  sources: SourceRef[];
}

export function fmt(value: number | null, unit: string, currency: string): string {
  if (value == null) return "—";
  if (unit === currency) return money(Math.round(value), currency);
  if (unit === "%") return `${Math.round(value * 10) / 10}%`;
  if (Math.abs(value) >= 1000) return Math.round(value).toLocaleString("en");
  return String(Math.round(value * 10) / 10);
}

export function Tiles({ items, currency }: { items: Figure[]; currency: string }) {
  return (
    <section className={s.tiles}>
      {items.map((f) => (
        <div
          key={f.key}
          className={`${s.tile} ${f.assumed ? s.assumed : ""} ${f.tone ? s[f.tone] : ""}`}
          data-testid={`tile-${f.key}`}
        >
          <div className={s.tileN}>
            {fmt(f.value, f.unit, currency)}
            {f.value != null && f.unit && f.unit !== currency && f.unit !== "%" && <small> {f.unit}</small>}
          </div>
          <div className={s.tileLabel}>{f.label}</div>
          <div className={s.tileSub}>{f.text}</div>
          {f.assumed && <span className={s.tag}>{t("demo / entered")}</span>}
          <Refs list={f.sources} />
        </div>
      ))}
    </section>
  );
}

export function Refs({ list, wide }: { list: SourceRef[]; wide?: boolean }) {
  if (!list.length) return null;
  const seen = new Set<string>();
  const rows = list.filter((e) => {
    const k = `${e.kind}:${e.ref}:${e.label}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return (
    <div className={s.refs}>
      {rows.map((e) => (
        <span
          key={`${e.kind}:${e.ref}:${e.label}`}
          className={`${s.ref} ${["rate_card", "entered", "demo"].includes(e.kind) ? s.refWeak : ""}`}
          title={`${e.kind.replace("_", " ")} — ${e.label}${e.enteredBy ? ` (${e.enteredBy})` : ""}`}
        >
          {e.href ? <Link to={e.href}>{e.ref}</Link> : e.ref}
          {wide && <em> {e.label}</em>}
        </span>
      ))}
    </div>
  );
}

export function Bars({
  rows,
  currency,
  unit,
}: {
  rows: { label: string; value: number; against: number; href?: string }[];
  currency: string;
  unit?: string;
}) {
  const max = Math.max(1, ...rows.map((r) => Math.max(r.value, r.against)));
  return (
    <div className={s.bars}>
      {rows.map((r) => {
        const over = r.value > r.against;
        return (
          <div key={r.label} className={s.bar}>
            <span>{r.href ? <Link to={r.href}>{r.label}</Link> : r.label}</span>
            <div className={s.barTrack}>
              <div
                className={`${s.barFill} ${over ? s.over : s.under}`}
                style={{ width: `${(100 * r.value) / max}%` }}
              />
            </div>
            <span className={s.barN}>
              {fmt(r.value, unit ?? currency, currency)}{" "}
              <em className={s.muted}>/ {fmt(r.against, unit ?? currency, currency)}</em>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function MonthStrip({
  months,
  currency,
}: {
  months: { month: string; revenue: number; cost: number; budget: number }[];
  currency: string;
}) {
  const max = Math.max(1, ...months.flatMap((m) => [m.revenue, m.cost, m.budget]));
  return (
    <div className={s.strip} aria-label={t("Revenue, cost and budget by month")}>
      {months.map((m) => (
        <div
          key={m.month}
          className={s.month}
          title={t("{month}: revenue {revenue}, cost {cost}, budget {budget}", {
            month: m.month,
            revenue: money(m.revenue, currency),
            cost: money(m.cost, currency),
            budget: money(m.budget, currency),
          })}
        >
          <div className={s.monthBars}>
            <i style={{ height: `${(90 * m.revenue) / max}px`, background: "var(--blue)" }} />
            <i style={{ height: `${(90 * m.cost) / max}px`, background: "var(--red)" }} />
            <i style={{ height: `${(90 * m.budget) / max}px`, background: "var(--line)" }} />
          </div>
          <span className={s.monthLabel}>{m.month.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * The question box. `own` answers from the engine; anything else goes to a
 * connected model with `material` and only that, and its reply is accepted
 * only when every figure in it is in `figures`.
 */
export function AskCard({
  title,
  sub,
  material,
  figures,
  own,
  prompt,
  placeholder,
  board,
}: {
  title: string;
  sub: string;
  material: string;
  figures: Set<string>;
  own: (q: string) => string | null;
  prompt: string;
  placeholder: string;
  board?: string[];
}) {
  const cfg = useMemo(() => loadConfig(), []);
  const connected = hasModel(cfg);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [modelText, setModelText] = useState<string | null>(null);
  const [modelNote, setModelNote] = useState<string | null>(null);
  const [answers, setAnswers] = useState<{ q: string; a: string; from: string }[]>([]);

  const challenge = async () => {
    setBusy(true);
    try {
      const reply = await askModel(
        cfg,
        prompt,
        `Argue against this reading of the figures, then say what you would do first.\n\n${material}`,
      );
      const bad = verifyFigures(reply.text, figures);
      if (bad.length) {
        setModelText(null);
        setModelNote(
          bad.length === 1
            ? t("The model's challenge was withheld: it stated a figure the module did not produce ({list}).", {
                list: bad.slice(0, 4).join(", "),
              })
            : t("The model's challenge was withheld: it stated figures the module did not produce ({list}).", {
                list: bad.slice(0, 4).join(", "),
              }),
        );
      } else {
        setModelText(reply.text);
        setModelNote(t("Challenged by {model}; every figure checked against the module.", { model: reply.model }));
      }
    } catch (e) {
      setModelNote(t("The model could not be reached: {error}", { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };
  const ask = async () => {
    const text = q.trim();
    if (!text) return;
    const mine = own(text);
    if (mine) {
      setAnswers((a) => [{ q: text, a: mine, from: t("the module") }, ...a]);
      setQ("");
      return;
    }
    if (!connected) {
      setAnswers((a) => [
        {
          q: text,
          a: t(
            "The module answers from its own figures — ask about a tile, a candidate, a rank or what to do first. Connect a model in Settings for anything wider.",
          ),
          from: t("the module"),
        },
        ...a,
      ]);
      setQ("");
      return;
    }
    setBusy(true);
    try {
      const reply = await askModel(cfg, prompt, `Question: ${text}\n\nMaterial:\n${material}`);
      const bad = verifyFigures(reply.text, figures);
      setAnswers((a) => [
        {
          q: text,
          a: bad.length
            ? t("Withheld: the reply stated figures the module did not produce ({list}).", {
                list: bad.slice(0, 4).join(", "),
              })
            : reply.text,
          from: bad.length ? t("withheld") : reply.model,
        },
        ...a,
      ]);
    } catch (e) {
      setAnswers((a) => [
        {
          q: text,
          a: t("The model could not be reached: {error}", { error: e instanceof Error ? e.message : String(e) }),
          from: t("error"),
        },
        ...a,
      ]);
    } finally {
      setBusy(false);
      setQ("");
    }
  };
  return (
    /* the ask card is tall on every desk it appears on, so it rolls up too;
       the key is its own title, which names the desk it belongs to */
    <Card collapsible title={title} sub={sub}>
      {board && (
        <div className={s.board}>
          {board.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}
      <div className={s.row}>
        <Button onClick={() => void challenge()} disabled={!connected || busy}>
          {connected
            ? busy
              ? t("Asking…")
              : t("Ask the model to argue against it")
            : t("No model connected — Settings › Sky › beyond the platform")}
        </Button>
        {modelNote && <span className={s.muted}>{modelNote}</span>}
      </div>
      {modelText && <blockquote className={s.model}>{modelText}</blockquote>}
      <form
        className={s.ask}
        onSubmit={(e) => {
          e.preventDefault();
          void ask();
        }}
      >
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          aria-label={t("Question for {title}", { title: t(title) })}
        />
        <Button type="submit" variant="primary" disabled={busy || !q.trim()}>
          {t("Ask")}
        </Button>
      </form>
      {answers.length > 0 && (
        <div className={s.answers}>
          {answers.map((a, i) => (
            <div key={i} className={s.answer}>
              <div className={s.q}>{a.q}</div>
              <p className={s.a}>{a.a}</p>
              <div className={s.from}>{a.from}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Every number in a piece of text, for a module's figure set. */
export function numbersIn(text: string): string[] {
  return text.match(/\d[\d,]*(?:\.\d+)?/g) ?? [];
}

/** The figure set a module hands to the checker: everything in its material, with and without thousands separators. */
export function figureSet(material: string, extra: (number | null | undefined)[] = []): Set<string> {
  const out = new Set<string>();
  for (const n of numbersIn(material)) {
    out.add(n);
    out.add(n.replace(/,/g, ""));
  }
  for (const n of extra) {
    if (n == null || Number.isNaN(n)) continue;
    out.add(String(Math.round(n)));
    out.add(String(Math.round(n * 10) / 10));
    out.add(Math.round(n).toLocaleString("en"));
  }
  for (let n = 0; n <= 12; n++) out.add(String(n));
  return out;
}
